import { useState, useCallback, useEffect } from "react";
import { apiEndpoints } from "../../../utils/api";
import { getRelativePath, isSandboxWorkspace } from "../utils/pathUtils";
import { SANDBOX_WORKSPACE_PATH } from "../constants";
import path from "path-browserify";

/**
 * Hook to manage file operations (loading, saving, directory browsing)
 * @param {string} workspaceId - The workspace ID
 * @param {Object} authenticatedApi - The authenticated API client
 * @param {string} projectPath - The project root path
 * @returns {Object} Object containing file operations and state
 */
export const useFileOperations = (
  workspaceId,
  authenticatedApi,
  projectPath,
) => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fileError, setFileError] = useState(null);
  const [loadingDirectories, setLoadingDirectories] = useState(new Set());
  const [loadingFile, setLoadingFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Load initial project files
  const loadProjectFiles = useCallback(async () => {
    if (!workspaceId || !authenticatedApi) {
      return;
    }

    try {
      setIsLoading(true);
      setFileError(null);

      const response = await authenticatedApi.get(
        apiEndpoints.workspaceFiles(workspaceId),
      );

      if (response?.files) {
        const filesList = response.files.map((file) => ({
          name: file.name,
          path: file.path,
          type: file.type,
          size: file.size,
          lastModified: file.modifiedTime,
        }));

        setFiles(filesList);

        // Set the initial expanded folder based on workspace type
        const isSandbox = filesList.some(
          (file) =>
            file.path.startsWith(SANDBOX_WORKSPACE_PATH + "/") ||
            file.path === SANDBOX_WORKSPACE_PATH,
        );
        const initialExpandPath = isSandbox
          ? SANDBOX_WORKSPACE_PATH
          : projectPath;
        setExpandedFolders({ [initialExpandPath]: true });
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (err) {
      console.error("Error loading project files:", err);
      setFileError(
        err.code === "ECONNABORTED"
          ? "Connection to server timed out"
          : `Error accessing project directory: ${err.message}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, authenticatedApi, projectPath]);

  // Load directory contents
  const loadDirectoryContents = useCallback(
    async (dirPath) => {
      if (!workspaceId || !authenticatedApi) return;

      try {
        // Handle both sandbox and local workspace paths
        let relativePath;

        if (dirPath.startsWith(SANDBOX_WORKSPACE_PATH + "/")) {
          // Sandbox workspace - extract relative path
          relativePath = dirPath.replace(SANDBOX_WORKSPACE_PATH + "/", "");
        } else if (dirPath === SANDBOX_WORKSPACE_PATH) {
          // Root sandbox workspace
          relativePath = "";
        } else {
          // Local workspace - use path.relative
          relativePath = path.relative(projectPath, dirPath);
        }

        setLoadingDirectories((prev) => new Set([...prev, dirPath]));

        const response = await authenticatedApi.get(
          `${apiEndpoints.workspaceFiles(workspaceId)}?path=${encodeURIComponent(relativePath)}`,
        );

        if (response?.files) {
          const newFiles = response.files.map((file) => ({
            name: file.name,
            path: file.path,
            type: file.type,
            size: file.size,
            lastModified: file.modifiedTime,
          }));

          setFiles((prevFiles) => {
            const fileMap = new Map();
            prevFiles.forEach((file) => fileMap.set(file.path, file));
            newFiles.forEach((file) => fileMap.set(file.path, file));
            return Array.from(fileMap.values());
          });

          setLoadingDirectories((prev) => {
            const newSet = new Set(prev);
            newSet.delete(dirPath);
            return newSet;
          });

          return {
            files: newFiles.filter((f) => f.type === "file"),
            directories: newFiles.filter((f) => f.type === "directory"),
          };
        }
        throw new Error(`Invalid response: ${JSON.stringify(response)}`);
      } catch (err) {
        console.error("Directory load error:", err);
        setLoadingDirectories((prev) => {
          const newSet = new Set(prev);
          newSet.delete(dirPath);
          return newSet;
        });
        throw err;
      }
    },
    [workspaceId, authenticatedApi, projectPath],
  );

  // Load file content
  const loadFileContent = useCallback(
    async (filePath) => {
      if (!workspaceId || !authenticatedApi) return;

      try {
        setLoadingFile(filePath);

        // For sandbox workspaces, the filePath is already absolute
        // For local workspaces, we need to calculate relative path
        let relativePath;

        if (filePath.startsWith(SANDBOX_WORKSPACE_PATH + "/")) {
          // Sandbox workspace - extract relative path from the absolute path
          relativePath = filePath.replace(SANDBOX_WORKSPACE_PATH + "/", "");
        } else if (filePath === SANDBOX_WORKSPACE_PATH) {
          // Root sandbox workspace
          relativePath = "";
        } else {
          // Local workspace - use path.relative as before
          relativePath = path.relative(projectPath, filePath);
        }

        const response = await authenticatedApi.post(
          apiEndpoints.workspaceFileContent(workspaceId),
          { path: relativePath },
        );

        if (response?.content !== undefined) {
          setLoadingFile(null);

          if (response.encoding === "base64") {
            return `[Binary file - ${response.content.length} bytes encoded]`;
          }
          return response.content;
        }
        throw new Error(`Invalid response format: ${JSON.stringify(response)}`);
      } catch (err) {
        console.error("File load error:", {
          error: err.message,
          filePath,
          response: err.response,
          config: err.config,
        });
        setLoadingFile(null);
        throw err;
      }
    },
    [workspaceId, authenticatedApi, projectPath],
  );

  // Save file
  const saveFile = useCallback(
    async (filePath, content, commitMessage = null) => {
      if (!workspaceId || !authenticatedApi) return;

      // Calculate relative path
      let relativePath = filePath;
      if (filePath.startsWith(SANDBOX_WORKSPACE_PATH + "/")) {
        relativePath = filePath.replace(SANDBOX_WORKSPACE_PATH + "/", "");
      } else if (filePath === SANDBOX_WORKSPACE_PATH) {
        relativePath = "";
      } else if (filePath.startsWith(projectPath + "/")) {
        relativePath = filePath.replace(projectPath + "/", "");
      } else if (filePath.startsWith(projectPath)) {
        relativePath = path.relative(projectPath, filePath);
      }

      const payload = {
        path: relativePath,
        content: content,
      };

      if (commitMessage) {
        payload.commit_message = commitMessage;
      }

      const response = await authenticatedApi.put(
        apiEndpoints.workspaceSaveFile(workspaceId),
        payload,
      );

      return response;
    },
    [workspaceId, authenticatedApi, projectPath],
  );

  // Toggle folder expansion
  const toggleFolder = useCallback(
    async (folderPath) => {
      const dirPath =
        typeof folderPath === "object" ? folderPath.path : folderPath;

      setExpandedFolders((prev) => ({
        ...prev,
        [dirPath]: !prev[dirPath],
      }));

      // Load directory contents if expanding and not already loaded
      if (!expandedFolders[dirPath]) {
        try {
          await loadDirectoryContents(dirPath);
        } catch (err) {
          console.error("Error loading directory contents:", err);
          // Optionally show error to user
        }
      }
    },
    [expandedFolders, loadDirectoryContents],
  );

  // Refresh files
  const refreshFiles = useCallback(async () => {
    await loadProjectFiles();
  }, [loadProjectFiles]);

  // Load initial files on mount
  // Match the original component's behavior exactly
  useEffect(() => {
    const loadFiles = async () => {
      if (!workspaceId || !authenticatedApi) {
        return;
      }

      try {
        setIsLoading(true);
        setFileError(null);

        const response = await authenticatedApi.get(
          apiEndpoints.workspaceFiles(workspaceId),
        );

        if (response?.files) {
          const filesList = response.files.map((file) => ({
            name: file.name,
            path: file.path,
            type: file.type,
            size: file.size,
            lastModified: file.modifiedTime,
          }));

          setFiles(filesList);

          // Set the initial expanded folder based on workspace type
          const isSandbox = filesList.some(
            (file) =>
              file.path.startsWith(SANDBOX_WORKSPACE_PATH + "/") ||
              file.path === SANDBOX_WORKSPACE_PATH,
          );
          const initialExpandPath = isSandbox
            ? SANDBOX_WORKSPACE_PATH
            : projectPath;
          setExpandedFolders({ [initialExpandPath]: true });
        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (err) {
        console.error("Error loading project files:", err);
        setFileError(
          err.code === "ECONNABORTED"
            ? "Connection to server timed out"
            : `Error accessing project directory: ${err.message}`,
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadFiles();
  }, [projectPath, workspaceId, authenticatedApi]); // Include all actual dependencies

  return {
    // State
    files,
    setFiles,
    isLoading,
    fileError,
    setFileError,
    loadingDirectories,
    loadingFile,
    expandedFolders,

    // Operations
    loadProjectFiles,
    loadDirectoryContents,
    loadFileContent,
    saveFile,
    toggleFolder,
    refreshFiles,
  };
};
